<template>
  <div class="modal-backdrop" @click.self="handleClose">
    <div class="modal-dialog">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bi bi-exclamation-triangle-fill text-danger me-2"></i>
            Delete Account
          </h5>
          <button type="button" class="btn-close" @click="handleClose"></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <div v-if="step === 1" class="step-1">
            <div class="warning-box">
              <h6 class="warning-title">Are you absolutely sure?</h6>
              <p class="warning-text">
                This action <strong>cannot be undone</strong>. This will permanently delete:
              </p>
              <ul class="deletion-list">
                <li><i class="bi bi-check2"></i> Your account credentials and login information</li>
                <li><i class="bi bi-check2"></i> Your personal profile and company information</li>
                <li><i class="bi bi-check2"></i> All your chat history and conversations</li>
                <li><i class="bi bi-check2"></i> Your solution recommendations and saved data</li>
              </ul>
            </div>
          </div>

          <div v-else class="step-2">
            <div class="confirmation-box">
              <p class="confirmation-text">
                Please type <strong>DELETE</strong> to confirm account deletion:
              </p>
              <input
                v-model="confirmText"
                type="text"
                class="form-control"
                placeholder="Type DELETE here"
                @keyup.enter="handleConfirm"
              />
              <p v-if="showError" class="error-text">
                Please type DELETE to proceed
              </p>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="handleClose"
          >
            Cancel
          </button>
          <button 
            v-if="step === 1"
            type="button" 
            class="btn btn-danger" 
            @click="step = 2"
          >
            Continue
          </button>
          <button 
            v-else
            type="button" 
            class="btn btn-danger" 
            @click="handleConfirm"
            :disabled="isDeleting"
          >
            <span v-if="isDeleting">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Deleting...
            </span>
            <span v-else>Delete My Account</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';

export default {
  name: 'DeleteAccountModal',
  emits: ['close', 'confirm'],
  setup(props, { emit }) {
    const step = ref(1);
    const confirmText = ref('');
    const showError = ref(false);
    const isDeleting = ref(false);

    const handleClose = () => {
      if (!isDeleting.value) {
        emit('close');
      }
    };

    const handleConfirm = async () => {
      if (confirmText.value !== 'DELETE') {
        showError.value = true;
        return;
      }

      isDeleting.value = true;
      emit('confirm');
    };

    return {
      step,
      confirmText,
      showError,
      isDeleting,
      handleClose,
      handleConfirm
    };
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  animation: fadeIn 0.2s ease;
}

.modal-dialog {
  width: 90%;
  max-width: 500px;
  animation: slideIn 0.3s ease;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
}

.btn-close {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: #999;
  cursor: pointer;
  transition: color 0.2s ease;
}

.btn-close:hover {
  color: #333;
}

.modal-body {
  padding: 2rem;
}

.warning-box {
  background-color: #fff5f5;
  border: 1px solid #fee;
  border-radius: 8px;
  padding: 1.5rem;
}

.warning-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #dc3545;
  margin-bottom: 1rem;
}

.warning-text {
  color: #666;
  margin-bottom: 1rem;
}

.deletion-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.deletion-list li {
  padding: 0.5rem 0;
  color: #666;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.deletion-list i {
  color: #dc3545;
  font-size: 1.1rem;
}

.confirmation-box {
  text-align: center;
}

.confirmation-text {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 1.5rem;
}

.form-control {
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  transition: border-color 0.2s ease;
}

.form-control:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  outline: none;
}

.error-text {
  color: #dc3545;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.btn {
  padding: 0.5rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 95%;
  }

  .modal-body {
    padding: 1.5rem;
  }
}
</style>